'use client';

/**
 * InterceptsFeed — THE INTERCEPTS, the production cut of the chain Social feed.
 *
 * Visual source of truth: docs/design/intercepts-prototype/intercepts.html +
 * intercepts-v2.html (SIGNAL grades + focus mode only). This component OWNS tweet
 * rendering for the dashboard surface (it replaces the generic TwitterFeed there)
 * so it can layer the intel desk affordances the cut requires:
 *
 *   · ON THE AIR band — entities ranked by 24h tweet volume (computed client-side
 *     from loaded tweets); chips focus the feed (hidden scrollbar).
 *   · SIGNAL grades + sort — per-tweet S/A/B/C pill from tweet_ratings.newsworthiness
 *     (read client-side; public collection), sort SIGNAL · LATEST · LOUDEST (default
 *     SIGNAL). Unrated tweets sort below rated ones in SIGNAL mode.
 *   · Provenance — "◆ CITED IN THE BRIEF" when a tweet id is in a recent recap's
 *     featuredTweets[] (server route /api/social/brief-citations).
 *   · Focus mode — clicking a roster row / ON THE AIR chip narrows the feed to one
 *     entity + a slim RECON header (entity-profile API). Deep-linkable ?entity=.
 *
 * DATA PATH (mirrors TwitterFeed exactly so cost/index behavior is identical):
 *   tweets   — Firestore web SDK, `where('isHidden','==',false)` + `handleId in [..30]`
 *              + `orderBy('createdAt','desc')`. Scatter-gather over chunks of 30 for
 *              chain handle lists > 30. No new index.
 *   ratings  — tweet_ratings/{tweetId}, `where(documentId() in [..30])`, chunked.
 *              Public read, doc id = tweetId. No index.
 *   profiles — twitterHandles/{id} for fresh proxied avatars + linked tags (as in
 *              TwitterFeed.fetchProfiles).
 *
 * Token-native (gold-only). All server→client data is plain JSON.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  documentId,
  Timestamp,
  type QueryConstraint,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { tagService } from '@/lib/tag-service';
import { chunkArray, FIRESTORE_IN_QUERY_LIMIT } from '@/lib/batch-fetcher';
import type { Tweet } from '@/types/twitter';
import type { Tag } from '@/types/tags';
import InterceptCard from './InterceptCard';

type SortMode = 'signal' | 'latest' | 'loudest';

const INITIAL_FETCH = 40; // dense cards → a full screen is many cards; fetch a deep first page
const MAX_HANDLES_PER_QUERY = FIRESTORE_IN_QUERY_LIMIT; // 30

interface FocusRecon {
  name: string;
  handle?: string;
  summary?: string;
  sentiment?: string;
  topics?: string[];
}

/** One tweet's rating — grade source + the desk's own words (tweet_ratings doc). */
interface RatingEntry {
  newsworthiness: number;
  /** The analyst's one-liner (tweet_ratings.summary, ≤200 chars) — DESK NOTE. */
  summary?: string;
  /** Compressed restatement (tweet_ratings.headline, ≤100 chars) — note fallback. */
  headline?: string;
}

export interface InterceptsFeedProps {
  /** Chain-scoped tracked handle ids (lowercased handleIds). */
  chainHandles?: string[];
  chainLabel?: string;
  /** THE TAPE: scrub the feed to one past UTC day (YYYY-MM-DD). Undefined = live. */
  dayFilter?: string;
  /** Focus mode: the entity slug currently focused (from ?entity= / roster click). */
  focusedSlug?: string;
  /** Focus mode: the entity's tracked handles (narrows the feed query). */
  focusedHandles?: string[];
  focusedName?: string;
  /** Clear focus (× in the focus header). */
  onClearFocus?: () => void;
  /** Focus an entity (ON THE AIR chip / in-card entity chip). */
  onEntitySelect?: (entityId: string, entitySlug: string) => void;
  /** Scoped social base for the OPEN DOSSIER link target prefix (entity dossier = /tags/[slug]). */
  entitySocialBase?: string;
  /** Header eyebrow/title region from the host (account count + freshness). */
  title?: React.ReactNode;
}

function engagement(t: Tweet): number {
  const m = t.metrics ?? ({} as Tweet['metrics']);
  return (m.likes || 0) + (m.retweets || 0) + (m.replies || 0);
}

function createdMs(t: Tweet): number {
  const ts: any = t.createdAt;
  if (!ts) return 0;
  if (ts.toDate) return ts.toDate().getTime();
  if (ts.seconds) return ts.seconds * 1000;
  return new Date(ts).getTime();
}

export default function InterceptsFeed({
  chainHandles,
  chainLabel,
  dayFilter,
  focusedSlug,
  focusedHandles,
  focusedName,
  onClearFocus,
  onEntitySelect,
  // Always overridden by DashboardSocialView with the chain-scoped base; default targets
  // the primary chain (bare `/social` index retired). TODO(multichain): chain-derive.
  entitySocialBase = '/abstract/social',
  title,
}: InterceptsFeedProps) {
  const [tweets, setTweets] = useState<Tweet[]>([]);
  const [profiles, setProfiles] = useState<Record<string, string>>({});
  const [handleTagMap, setHandleTagMap] = useState<Map<string, Tag[]>>(new Map());
  const [ratings, setRatings] = useState<Record<string, RatingEntry>>({});
  const [ratingsAvailable, setRatingsAvailable] = useState<boolean | null>(null); // null = unknown yet
  const [citedIds, setCitedIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [sort, setSort] = useState<SortMode>('signal');
  const [recon, setRecon] = useState<FocusRecon | null>(null);

  // The handle set the feed queries: the focused entity's handles when focused,
  // else the chain roster. Lowercased to match handleId doc-ids.
  const activeHandles = useMemo(() => {
    const src = focusedSlug ? focusedHandles : chainHandles;
    return (src ?? []).map((h) => h.replace(/^@/, '').toLowerCase()).filter(Boolean);
  }, [focusedSlug, focusedHandles, chainHandles]);

  const handleKey = activeHandles.join(',');

  // All tracked handle ids on this surface — used to flag quoted authors that are
  // themselves in the roster (an in-graph edge, gold-edged on the card).
  const trackedSet = useMemo(() => {
    const s = new Set<string>();
    for (const h of chainHandles ?? []) s.add(h.replace(/^@/, '').toLowerCase());
    for (const h of activeHandles) s.add(h);
    return s;
  }, [chainHandles, activeHandles]);

  // ── Fetch tweets (initial page; "load more" omitted this wave — INITIAL_FETCH is deep) ──
  const fetchTweets = useCallback(async () => {
    if (!db) {
      setError('Feed unavailable');
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      // THE TAPE: when scrubbed to a past day, bound the SAME queries with a
      // createdAt range. A range on the existing orderBy field rides the existing
      // composite indexes (isHidden[+handleId]+createdAt) — NO new index.
      const dayConstraints: QueryConstraint[] = [];
      if (dayFilter) {
        const dayStartMs = Date.parse(`${dayFilter}T00:00:00.000Z`);
        if (!Number.isNaN(dayStartMs)) {
          dayConstraints.push(
            where('createdAt', '>=', Timestamp.fromMillis(dayStartMs)),
            where('createdAt', '<', Timestamp.fromMillis(dayStartMs + 86_400_000)),
          );
        }
      }

      let fetched: Tweet[] = [];
      if (activeHandles.length === 0) {
        // No scoped handles → mirror TwitterFeed's "no filter" firehose (latest tweets).
        const snap = await getDocs(
          query(
            collection(db, 'tweets'),
            where('isHidden', '==', false),
            ...dayConstraints,
            orderBy('createdAt', 'desc'),
            limit(INITIAL_FETCH),
          ),
        );
        fetched = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Tweet);
      } else if (activeHandles.length <= MAX_HANDLES_PER_QUERY) {
        const snap = await getDocs(
          query(
            collection(db, 'tweets'),
            where('isHidden', '==', false),
            where('handleId', 'in', activeHandles),
            ...dayConstraints,
            orderBy('createdAt', 'desc'),
            limit(INITIAL_FETCH),
          ),
        );
        fetched = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Tweet);
      } else {
        // Scatter-gather: one ordered+limited query per chunk of 30, merged client-side.
        const chunks = chunkArray(activeHandles, MAX_HANDLES_PER_QUERY);
        const snaps = await Promise.all(
          chunks.map((chunk) =>
            getDocs(
              query(
                collection(db!, 'tweets'),
                where('isHidden', '==', false),
                where('handleId', 'in', chunk),
                ...dayConstraints,
                orderBy('createdAt', 'desc'),
                limit(INITIAL_FETCH),
              ),
            ),
          ),
        );
        const all = snaps.flatMap((snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Tweet));
        all.sort((a, b) => createdMs(b) - createdMs(a));
        fetched = all.slice(0, INITIAL_FETCH);
      }

      // Drop replies-to-others (conversation tails), but KEEP self-threads (the handle
      // replying to itself — popular continuations). Matches TwitterFeed.
      const isSelfThread = (t: Tweet) =>
        !!t.isReply && !!t.inReplyToUsername && !!t.username &&
        t.inReplyToUsername.toLowerCase() === t.username.toLowerCase();
      fetched = fetched.filter((t) => (!t.isReply || isSelfThread(t)) && !t.isHidden);
      setTweets(fetched);

      // Fan out the dependent reads (profiles, ratings) — never block the paint on them.
      void fetchProfiles(fetched);
      void fetchRatings(fetched);
    } catch (err: any) {
      console.error('[InterceptsFeed] tweet fetch failed:', err);
      setError(err?.message || 'Failed to load intercepts');
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleKey, dayFilter]);

  // ── Fetch fresh proxied avatars + linked tags (mirrors TwitterFeed.fetchProfiles) ──
  const fetchProfiles = async (list: Tweet[]) => {
    if (!db) return;
    try {
      const ids = Array.from(new Set(list.map((t) => t.handleId)));
      const fallback: Record<string, string> = {};
      list.forEach((t) => {
        if (t.authorProfilePic) fallback[t.handleId] = t.authorProfilePic;
      });

      const profileMap: Record<string, string> = {};
      const handleTagData: { handleId: string; linkedTagIds: string[] }[] = [];

      for (let i = 0; i < ids.length; i += 10) {
        const batch = ids.slice(i, i + 10);
        if (batch.length === 0) continue;
        const snap = await getDocs(
          query(collection(db, 'twitterHandles'), where(documentId(), 'in', batch)),
        );
        snap.docs.forEach((doc) => {
          const data = doc.data();
          const img = data.profileImage || data.profilePicUrl;
          if (img) profileMap[doc.id] = img;
          if (data.linkedTags?.length > 0) {
            handleTagData.push({ handleId: doc.id, linkedTagIds: data.linkedTags });
          }
        });
      }
      ids.forEach((id) => {
        if (!profileMap[id] && fallback[id]) profileMap[id] = fallback[id];
      });
      setProfiles((prev) => ({ ...prev, ...profileMap }));

      if (handleTagData.length > 0) {
        const allTagIds = new Set<string>();
        handleTagData.forEach((h) => h.linkedTagIds.forEach((id) => allTagIds.add(id)));
        if (allTagIds.size > 0) {
          const tagMap = await tagService.getTagsBatch(Array.from(allTagIds));
          const next = new Map<string, Tag[]>();
          handleTagData.forEach(({ handleId, linkedTagIds }) => {
            const tags = linkedTagIds
              .map((id) => tagMap.get(id))
              .filter((t): t is Tag => t !== undefined && t.isActive);
            if (tags.length > 0) next.set(handleId, tags);
          });
          setHandleTagMap((prev) => new Map([...prev, ...next]));
        }
      }
    } catch (err) {
      console.error('[InterceptsFeed] profile fetch failed:', err);
    }
  };

  // ── Fetch SIGNAL grades from tweet_ratings by id (public read, doc id = tweetId) ──
  const fetchRatings = async (list: Tweet[]) => {
    if (!db) {
      setRatingsAvailable(false);
      return;
    }
    try {
      const ids = list.map((t) => t.id);
      if (ids.length === 0) {
        setRatingsAvailable(false);
        return;
      }
      const map: Record<string, RatingEntry> = {};
      const chunks = chunkArray(ids, MAX_HANDLES_PER_QUERY);
      const snaps = await Promise.all(
        chunks.map((chunk) =>
          getDocs(query(collection(db!, 'tweet_ratings'), where(documentId(), 'in', chunk))),
        ),
      );
      snaps.forEach((snap) =>
        snap.docs.forEach((doc) => {
          const data = doc.data();
          const n = data?.newsworthiness;
          if (typeof n === 'number') {
            // Keep the desk's own words too — the DESK NOTE rendered on S/A cards.
            map[doc.id] = {
              newsworthiness: n,
              summary: typeof data.summary === 'string' ? data.summary : undefined,
              headline: typeof data.headline === 'string' ? data.headline : undefined,
            };
          }
        }),
      );
      setRatings((prev) => ({ ...prev, ...map }));
      // Available iff we actually got at least one grade — otherwise degrade to LATEST.
      setRatingsAvailable(Object.keys(map).length > 0);
    } catch (err) {
      console.error('[InterceptsFeed] ratings fetch failed (degrading to LATEST):', err);
      setRatingsAvailable(false);
    }
  };

  // ── Provenance: today's brief citations (server route, Admin SDK). Fetched once. ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/social/brief-citations/', { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && Array.isArray(data?.tweetIds)) {
          setCitedIds(new Set(data.tweetIds as string[]));
        }
      } catch {
        /* graceful: no badges */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Refetch when the active handle set changes (focus / chain switch).
  useEffect(() => {
    fetchTweets();
  }, [fetchTweets]);

  // Once ratings are known to be unavailable, fall back to LATEST as the default.
  useEffect(() => {
    if (ratingsAvailable === false && sort === 'signal') setSort('latest');
  }, [ratingsAvailable, sort]);

  // ── Focus RECON header (entity-profile API). Only when focused. ──
  useEffect(() => {
    if (!focusedSlug) {
      setRecon(null);
      return;
    }
    let cancelled = false;
    setRecon({ name: focusedName ?? focusedSlug }); // optimistic name while loading
    (async () => {
      try {
        const res = await fetch(`/api/entity-profile/${encodeURIComponent(focusedSlug)}`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setRecon({
          name: data?.entity?.name ?? focusedName ?? focusedSlug,
          handle: data?.entity?.handle,
          summary: data?.recon?.summary,
          sentiment: data?.recon?.sentiment,
          topics: Array.isArray(data?.recon?.topics) ? data.recon.topics : [],
        });
      } catch {
        /* keep the optimistic name-only header */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [focusedSlug, focusedName]);

  // ── ON THE AIR — entities ranked by 24h volume, computed from loaded tweets.
  // Hidden in archive mode (the archive banner owns the day's framing there). ──
  const onTheAir = useMemo(() => {
    const cutoff = dayFilter ? 0 : Date.now() - 24 * 3_600_000;
    const counts = new Map<string, { handleId: string; name: string; entityId?: string; slug?: string; count: number }>();
    for (const t of tweets) {
      if (createdMs(t) < cutoff) continue;
      const tags = handleTagMap.get(t.handleId);
      const tag = tags && tags.length > 0 ? tags[0] : null;
      const key = tag?.slug ?? t.handleId;
      const prev = counts.get(key);
      if (prev) prev.count += 1;
      else
        counts.set(key, {
          handleId: t.handleId,
          name: tag?.name ?? t.username ?? t.handleId,
          entityId: tag?.id,
          slug: tag?.slug,
          count: 1,
        });
    }
    return Array.from(counts.values()).sort((a, b) => b.count - a.count);
  }, [tweets, handleTagMap, dayFilter]);

  // ── Sorted, displayed tweets ──
  const displayed = useMemo(() => {
    const list = [...tweets];
    if (sort === 'latest') {
      list.sort((a, b) => createdMs(b) - createdMs(a));
    } else if (sort === 'loudest') {
      list.sort((a, b) => engagement(b) - engagement(a));
    } else {
      // SIGNAL: rated tweets by newsworthiness desc; unrated sink below, then by recency.
      list.sort((a, b) => {
        const ra = ratings[a.id]?.newsworthiness;
        const rb = ratings[b.id]?.newsworthiness;
        const ha = typeof ra === 'number';
        const hb = typeof rb === 'number';
        if (ha && hb) return rb! - ra!;
        if (ha) return -1;
        if (hb) return 1;
        return createdMs(b) - createdMs(a);
      });
    }
    return list;
  }, [tweets, sort, ratings]);

  const showSignal = ratingsAvailable !== false;

  return (
    <div>
      {/* ON THE AIR band — only in LIVE browse mode (focus already names one entity;
          archive mode's framing belongs to the archive banner). */}
      {!focusedSlug && !dayFilter && onTheAir.length > 0 && (
        <OnTheAirBand
          rows={onTheAir}
          chainLabel={chainLabel}
          onSelect={(r) => {
            if (r.entityId && r.slug) onEntitySelect?.(r.entityId, r.slug);
          }}
        />
      )}

      {/* Focus header — slim RECON one-liner + OPEN DOSSIER + ×. */}
      {focusedSlug && recon && (
        <FocusHeader recon={recon} slug={focusedSlug} onClear={onClearFocus} />
      )}

      {/* Feed header: title (from host) + SIGNAL · LATEST · LOUDEST sort. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          margin: '16px 0 12px',
          paddingBottom: 12,
          borderBottom: '0.5px solid var(--divider)',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ minWidth: 0 }}>{title}</div>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            border: '0.5px solid var(--divider)',
            background: 'var(--surface)',
            borderRadius: 5,
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 9.5,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--slate)',
              padding: '0 9px 0 11px',
            }}
          >
            Sort
          </span>
          {(['signal', 'latest', 'loudest'] as SortMode[]).map((m) => {
            if (m === 'signal' && !showSignal) return null;
            const on = sort === m;
            return (
              <button
                key={m}
                type="button"
                onClick={() => setSort(m)}
                aria-pressed={on}
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  padding: '6px 12px',
                  color: on ? 'var(--amber)' : 'var(--parchment-dim)',
                  background: on ? 'var(--amber-dim)' : 'transparent',
                  borderLeft: '0.5px solid var(--divider)',
                  cursor: 'pointer',
                }}
              >
                {m}
              </button>
            );
          })}
        </div>
      </div>

      {/* THE FEED */}
      {isLoading ? (
        <FeedSkeleton />
      ) : error ? (
        <div
          style={{
            background: 'var(--surface)',
            border: '0.5px solid var(--divider)',
            borderRadius: 6,
            padding: '24px 18px',
            textAlign: 'center',
            fontFamily: 'var(--font-mono)',
            fontSize: 12,
            color: 'var(--parchment-dim)',
          }}
        >
          {error}
        </div>
      ) : displayed.length === 0 ? (
        <div
          style={{
            background: 'var(--surface)',
            border: '0.5px solid var(--divider)',
            borderRadius: 6,
            padding: '28px 20px',
            textAlign: 'center',
            fontFamily: 'var(--font-mono)',
            fontSize: 12,
            color: 'var(--parchment-dim)',
          }}
        >
          {dayFilter
            ? 'Quiet day — no intercepts on the record for this date.'
            : 'No intercepts on this frequency.'}
        </div>
      ) : (
        <div className="intercepts-feed">
          {displayed.map((t) => {
            const rating = showSignal ? ratings[t.id] : undefined;
            return (
              <InterceptCard
                key={t.id}
                tweet={t}
                profileImage={profiles[t.handleId] || t.authorProfilePic}
                linkedTags={handleTagMap.get(t.handleId)}
                newsworthiness={rating?.newsworthiness}
                deskNote={rating?.summary || rating?.headline}
                cited={citedIds.has(t.id)}
                hideEntityTag={!!focusedSlug}
                onEntityClick={onEntitySelect}
                quotedIsTracked={
                  !!t.quotedTweet?.username &&
                  trackedSet.has(t.quotedTweet.username.toLowerCase())
                }
              />
            );
          })}
        </div>
      )}

      {/* Two-column feed ≥1440px (CSS columns; cards never split). Scrollbar-hidden
          ON THE AIR strip. Injected via dangerouslySetInnerHTML — React escapes the
          text children of a <style> tag (broken CSS + hydration mismatch otherwise). */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
            .ota-strip::-webkit-scrollbar{display:none}
            .ota-strip{scrollbar-width:none}
            @media (min-width:1440px){
              .intercepts-feed{column-count:2;column-gap:22px}
            }
          `,
        }}
      />
    </div>
  );
}

/* ── ON THE AIR band ─────────────────────────────────────────────────────────── */
function OnTheAirBand({
  rows,
  chainLabel,
  onSelect,
}: {
  rows: Array<{ handleId: string; name: string; entityId?: string; slug?: string; count: number }>;
  chainLabel?: string;
  onSelect: (r: { entityId?: string; slug?: string }) => void;
}) {
  const total = rows.reduce((s, r) => s + r.count, 0);
  const max = rows[0]?.count ?? 0;
  return (
    <div
      style={{
        border: '0.5px solid var(--divider)',
        borderTop: '2px solid var(--amber)',
        borderRadius: 5,
        background: 'var(--surface)',
        padding: '13px 16px 14px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 11 }}>
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: 'var(--amber)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span
            style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--amber)', boxShadow: '0 0 7px var(--amber)' }}
          />
          On the air · last 24h
        </span>
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 9.5,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'var(--parchment-dim)',
            marginLeft: 'auto',
          }}
        >
          {rows.length} {rows.length === 1 ? 'account' : 'accounts'} · {total} intercepts · ranked by volume
        </span>
      </div>
      <div className="ota-strip" style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 3 }}>
        {rows.map((r) => {
          const hot = r.count >= Math.max(2, max * 0.6);
          const clickable = !!(r.entityId && r.slug);
          return (
            <button
              key={r.slug ?? r.handleId}
              type="button"
              disabled={!clickable}
              onClick={() => clickable && onSelect(r)}
              style={{
                flexShrink: 0,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                border: `0.5px solid ${hot ? 'var(--amber-line)' : 'var(--divider)'}`,
                background: hot ? 'var(--amber-dim)' : 'var(--surface-2)',
                borderRadius: 999,
                padding: '6px 12px 6px 12px',
                cursor: clickable ? 'pointer' : 'default',
              }}
              title={clickable ? `Focus ${r.name}` : r.name}
            >
              <span
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontWeight: 600,
                  fontSize: 12.5,
                  letterSpacing: '-0.01em',
                  color: 'var(--parchment)',
                  whiteSpace: 'nowrap',
                }}
              >
                {r.name}
              </span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 600, color: 'var(--amber)' }}>
                {r.count}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── Focus header — slim RECON one-liner + OPEN DOSSIER + × ─────────────────────── */
const SENT_STYLE: Record<string, React.CSSProperties> = {
  positive: { color: 'var(--green-text)', background: 'var(--green-dim)', border: '0.5px solid rgba(85,201,133,0.35)' },
  bullish: { color: 'var(--green-text)', background: 'var(--green-dim)', border: '0.5px solid rgba(85,201,133,0.35)' },
  negative: { color: 'var(--red-text)', background: 'var(--red-dim)', border: '0.5px solid rgba(224,92,74,0.45)' },
  bearish: { color: 'var(--red-text)', background: 'var(--red-dim)', border: '0.5px solid rgba(224,92,74,0.45)' },
};

function FocusHeader({
  recon,
  slug,
  onClear,
}: {
  recon: FocusRecon;
  slug: string;
  onClear?: () => void;
}) {
  const sentKey = (recon.sentiment ?? '').toLowerCase();
  const sentStyle = SENT_STYLE[sentKey];
  return (
    <div
      style={{
        border: '0.5px solid var(--amber-line)',
        borderRadius: 5,
        background: 'var(--surface)',
        padding: '13px 16px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: recon.summary ? 7 : 0, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'var(--font-sans)', fontWeight: 700, fontSize: 14, letterSpacing: '-0.01em', color: 'var(--parchment)' }}>
          {recon.name}
        </span>
        {recon.handle && (
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--slate)' }}>@{recon.handle}</span>
        )}
        {sentStyle && (
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              padding: '2px 8px',
              borderRadius: 3,
              ...sentStyle,
            }}
          >
            {sentKey}
          </span>
        )}
        <div style={{ marginLeft: 'auto', display: 'inline-flex', gap: 8, alignItems: 'center' }}>
          <a
            href={`/tags/${slug}`}
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 9.5,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--amber)',
              borderBottom: '1px solid var(--amber-line)',
              textDecoration: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            Open dossier →
          </a>
          {onClear && (
            <button
              type="button"
              onClick={onClear}
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 9.5,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'var(--parchment-dim)',
                border: '0.5px solid var(--divider)',
                borderRadius: 4,
                padding: '4px 9px',
                background: 'transparent',
                cursor: 'pointer',
              }}
            >
              ✕ Clear
            </button>
          )}
        </div>
      </div>
      {recon.summary && (
        <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: '#D8D8D4', maxWidth: '78ch' }}>{recon.summary}</p>
      )}
      {recon.topics && recon.topics.length > 0 && (
        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {recon.topics.map((tp) => (
            <span
              key={tp}
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 9.5,
                letterSpacing: '0.04em',
                color: 'var(--parchment-dim)',
                border: '0.5px solid var(--divider)',
                borderRadius: 999,
                padding: '2px 9px',
              }}
            >
              {tp}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Skeleton ───────────────────────────────────────────────────────────────── */
function FeedSkeleton() {
  return (
    <div>
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} style={{ padding: '14px 0 14px 12px', borderBottom: '0.5px solid var(--divider)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 10 }}>
            <div style={{ width: 24, height: 24, borderRadius: 6, background: 'var(--surface-2)', flexShrink: 0 }} className="animate-pulse" />
            <div style={{ height: 11, width: 120, background: 'var(--surface-2)', borderRadius: 3 }} className="animate-pulse" />
          </div>
          <div style={{ height: 11, width: '90%', background: 'var(--surface-2)', borderRadius: 3, marginBottom: 6 }} className="animate-pulse" />
          <div style={{ height: 11, width: '70%', background: 'var(--surface-2)', borderRadius: 3 }} className="animate-pulse" />
        </div>
      ))}
    </div>
  );
}
